import { useState } from "react";
import { motion } from "framer-motion";
import { Crown, Headphones, Highlighter, Moon, NotebookPen, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GUIDE_ICON } from "./Guide";

const TIPS = [
  { icon: Highlighter, t: "Tap any verse", d: "Highlight it in one of five colours, or select a few verses at once." },
  { icon: NotebookPen, t: "Write a note", d: "Tap the pen on a highlight. Your notes gather in My Notes, ready to print." },
  { icon: Headphones, t: "Listen along", d: "Press Listen, or Play from here on a verse, and watch each verse light up." },
  { icon: Moon, t: "Make it yours", d: "Switch day and night, pick a page colour and set the text size." },
];

export function PlusWelcome() {
  const { user, setUser } = useAuth();
  const [gone, setGone] = useState(false);
  if (gone || !user?.is_plus || user?.plus_welcomed) return null;
  const close = () => { setGone(true); api.post("/me/plus-welcomed").catch(() => {}); setUser({ ...user, plus_welcomed: true }); };
  return (
    <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 18 }} data-testid="plus-welcome"
      className="relative mb-10 overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E293B] via-[#26344d] to-[#3b2f2a] p-7 text-amber-50 sm:p-10">
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-300/20 blur-3xl" />
      <button data-testid="plus-welcome-close" aria-label="Close" onClick={close} className="absolute right-4 top-4 rounded-full p-2 text-amber-100/70 hover:bg-white/10"><X className="h-5 w-5" /></button>
      <div className="relative flex items-center gap-4 pr-8">
        <img src={GUIDE_ICON} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-amber-300" />
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-amber-300"><Crown className="h-4 w-4" />Welcome to Cast Plus</p>
          <h2 className="mt-1 font-display text-3xl font-semibold sm:text-4xl">The whole Bible is open to you{user.name ? `, ${user.name.split(" ")[0]}` : ""}.</h2>
        </div>
      </div>
      <div className="relative mt-7 grid gap-4 sm:grid-cols-2">
        {TIPS.map((x, i) => (
          <motion.div key={x.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }} className="flex gap-3 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10" data-testid={`plus-welcome-tip-${i}`}>
            <x.icon className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
            <span><span className="block font-semibold">{x.t}</span><span className="text-sm text-amber-100/80">{x.d}</span></span>
          </motion.div>
        ))}
      </div>
      <p className="relative mt-6 text-sm text-amber-100/80">Stuck on a passage? Tap the Study Guide in any chapter. It answers from Scripture and can quiz you too.</p>
      <button data-testid="plus-welcome-start" onClick={close} className="relative mt-6 rounded-full bg-amber-300 px-6 py-3 text-sm font-semibold text-amber-950 transition-transform hover:-translate-y-0.5">Start reading</button>
    </motion.section>
  );
}
